import axios from 'axios';
import { showError } from '../components/CustomAlert';

// Frontend API Configuration


// Production API (override with REACT_APP_API_BASE_URL for local backend testing)
export const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'https://cclcusa.org/admin/backend/api';




//Ignore this line for now: export const API_BASE_URL = 'https://cclctraining.org/admin/backend/api'; Not working

export class SessionExpiredError extends Error {
  constructor() {
    super('Session expired');
    this.name = 'SessionExpiredError';
  }
}

export const isSessionExpired = (error) =>
  error?.name === 'SessionExpiredError' || error instanceof SessionExpiredError;

let sessionExpiryHandled = false;

const getStore = () => require('../store').store;

const getRequestUrl = (input) => {
  if (typeof input === 'string') return input;
  if (input instanceof Request) return input.url;
  return String(input);
};

const isAuthEndpoint = (requestUrl = '') =>
  requestUrl.includes('/auth/login') ||
  requestUrl.includes('/auth/signup') ||
  requestUrl.includes('/auth/verify');

const isStudentPortalAuthEndpoint = (requestUrl = '') =>
  requestUrl.includes('/student-login') ||
  requestUrl.includes('/enrollment-forms');

const isApiRequest = (requestUrl = '') =>
  requestUrl.startsWith(API_BASE_URL) || requestUrl.includes('/admin/backend/api');

const getHeaderValue = (headers, name) => {
  if (!headers) return '';
  const lower = name.toLowerCase();
  if (headers instanceof Headers) {
    return headers.get(name) || headers.get(lower) || '';
  }
  if (Array.isArray(headers)) {
    const entry = headers.find(([key]) => String(key).toLowerCase() === lower);
    return entry ? entry[1] : '';
  }
  return headers[name] || headers[lower] || headers.Authorization || headers.authorization || '';
};

const requestSentBearerToken = (input, init) => {
  const fromInit = getHeaderValue(init?.headers, 'Authorization');
  if (fromInit && String(fromInit).startsWith('Bearer ')) return true;
  if (input instanceof Request) {
    const fromRequest = getHeaderValue(input.headers, 'Authorization');
    return !!(fromRequest && String(fromRequest).startsWith('Bearer '));
  }
  return false;
};

const isTokenAuthFailureMessage = (message) => {
  const text = String(message || '').trim();
  return text === 'Token is not valid' || text === 'Token expired';
};

const isMissingTokenMessage = (message) =>
  String(message || '').trim() === 'No token, authorization denied';

async function read401Message(response) {
  try {
    const data = await response.clone().json();
    return data?.message || data?.error || '';
  } catch {
    return '';
  }
}

const shouldConsiderSessionExpiry = (requestUrl = '') => {
  if (!isApiRequest(requestUrl) || isAuthEndpoint(requestUrl) || isStudentPortalAuthEndpoint(requestUrl)) {
    return false;
  }
  const token = localStorage.getItem('token');
  const { isAuthenticated } = getStore().getState().auth || {};
  return !!(token || isAuthenticated);
};

const hasStoredToken = () => !!localStorage.getItem('token');

const isAuthenticatedSession = () => {
  try {
    return !!getStore().getState().auth?.isAuthenticated;
  } catch {
    return false;
  }
};

// Decide whether a 401 means the login session is dead and the user must re-auth.
//
// - "Token expired" / "Token is not valid": the bearer we sent was rejected → expired.
// - "No token, authorization denied": ambiguous, so disambiguate carefully:
//     * If we DID send a bearer, the token was rejected → expired.
//     * If we did NOT send one but a token still exists in localStorage, this is a
//       specific request that simply forgot its auth header (e.g. /particulars) —
//       the session is fine, so do NOT log out.
//     * If we did NOT send one AND no token exists while the app still believes it's
//       authenticated, the session is broken (token lost) → log out and re-auth.
const isSessionExpiry401 = (message, { sentBearerToken }) => {
  if (isTokenAuthFailureMessage(message)) {
    return true;
  }
  if (isMissingTokenMessage(message)) {
    if (sentBearerToken) {
      return true;
    }
    if (!hasStoredToken() && isAuthenticatedSession()) {
      return true;
    }
  }
  return false;
};

export const handleUnauthorized = (requestUrl = '') => {
  if (sessionExpiryHandled || !shouldConsiderSessionExpiry(requestUrl)) {
    return false;
  }

  sessionExpiryHandled = true;
  getStore().dispatch({ type: 'auth/logout' });
  showError('Your login token has expired. Please log in again.', 'Session Expired');
  setTimeout(() => {
    window.location.href = '/admin/login';
  }, 2000);
  return true;
};

// Auth headers for fetch-based API calls (applications, sessions, etc.)
export const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const nativeFetch = window.fetch.bind(window);

window.fetch = async (input, init) => {
  const requestUrl = getRequestUrl(input);
  const response = await nativeFetch(input, init);

  if (response.status === 401 && shouldConsiderSessionExpiry(requestUrl)) {
    const message = await read401Message(response);
    const sentBearerToken = requestSentBearerToken(input, init);
    if (isSessionExpiry401(message, { sentBearerToken }) && handleUnauthorized(requestUrl)) {
      throw new SessionExpiredError();
    }
  }

  return response;
};

// Request interceptor to add auth token
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
axios.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      const requestUrl = error.config?.url || '';
      const message = error.response?.data?.message || error.response?.data?.error || '';
      const sentBearerToken = !!(error.config?.headers?.Authorization || localStorage.getItem('token'));
      if (
        shouldConsiderSessionExpiry(requestUrl) &&
        isSessionExpiry401(message, { sentBearerToken }) &&
        handleUnauthorized(requestUrl)
      ) {
        return Promise.reject(new SessionExpiredError());
      }
    }
    return Promise.reject(error);
  }
);
